import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money, STATUS_LABELS, PAYMENT_LABELS, TRACK_STAGES } from "@/lib/format";
import { Search, Loader2, CheckCircle2, Circle, XCircle } from "lucide-react";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNum, setOrderNum] = useState(params.get("order") || "");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    if (!orderNum || !phone) {
      setError("Enter your order number and phone.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/orders/track", { params: { order_number: parseInt(orderNum), phone } });
      setOrder(data);
    } catch (e) {
      setOrder(null);
      setError(apiErr(e, "Order not found."));
    } finally {
      setLoading(false);
    }
  };

  const stageIndex = order ? TRACK_STAGES.indexOf(
    order.order_status === "out_for_delivery" ? "ready_for_pickup"
    : order.order_status === "delivered" ? "completed"
    : order.order_status
  ) : -1;
  const cancelled = order?.order_status === "cancelled";

  const field = "bg-input border-gold/25 text-cream placeholder:text-cream/30 rounded-xl h-12";

  return (
    <div className="max-w-2xl mx-auto px-4 py-14">
      <div className="text-center mb-8">
        <h1 className="font-serif text-4xl font-black text-cream">Track Your Order</h1>
        <p className="text-cream/60 mt-3">Enter your order number and phone to see live status.</p>
      </div>

      <form onSubmit={search} className="gold-border-card rounded-2xl bg-card-surface p-6 grid sm:grid-cols-2 gap-4 mb-8">
        <div><Label className="text-cream/70 text-sm">Order Number</Label><Input className={field} placeholder="e.g. 1042" value={orderNum} onChange={(e) => setOrderNum(e.target.value)} data-testid="track-order-number" /></div>
        <div><Label className="text-cream/70 text-sm">Phone Number</Label><Input className={field} placeholder="Your phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="track-phone" /></div>
        <Button type="submit" className="btn-gold rounded-full py-6 font-semibold sm:col-span-2" disabled={loading} data-testid="track-search-btn">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <span className="flex items-center gap-2"><Search size={16} /> Track Order</span>}
        </Button>
        {error && <p className="text-destructive text-sm text-center sm:col-span-2" data-testid="track-error">{error}</p>}
      </form>

      {order && (
        <div className="gold-border-card rounded-2xl bg-card-surface p-6" data-testid="track-result">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-mono text-gold text-xl">#{order.order_number}</p>
              <p className="text-cream/60 text-sm">{order.customer.first_name} {order.customer.last_name}</p>
            </div>
            <div className="text-right">
              <p className="text-cream/50 text-xs">Payment</p>
              <p className="text-gold text-sm font-medium">{PAYMENT_LABELS[order.payment_status]}</p>
            </div>
          </div>

          {cancelled ? (
            <div className="flex items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-xl p-4 text-destructive">
              <XCircle size={22} /> <span className="font-semibold">This order was cancelled.</span>
            </div>
          ) : (
            <div className="space-y-1" data-testid="track-progress">
              {TRACK_STAGES.map((s, i) => {
                const done = i <= stageIndex;
                const current = i === stageIndex;
                const label = order.fulfillment_method !== "pickup" && s === "ready_for_pickup"
                  ? (order.order_status === "out_for_delivery" || order.order_status === "delivered" ? "Out for Delivery" : "Ready / Out for Delivery")
                  : STATUS_LABELS[s];
                return (
                  <div key={s} className="flex items-center gap-3 py-2">
                    {done ? <CheckCircle2 className="text-gold" size={22} /> : <Circle className="text-cream/25" size={22} />}
                    <span className={`${current ? "text-gold font-semibold" : done ? "text-cream" : "text-cream/40"}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-gold/20 mt-5 pt-4 space-y-2">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm text-cream/80">
                <span>{it.quantity}× {it.category === "cookie" ? `Cookie Pair (${it.size_label})` : `${it.name} — ${it.size_label}`}</span>
                <span className="font-mono">{money(it.line_total)}</span>
              </div>
            ))}
            <div className="flex justify-between text-cream font-semibold pt-2 border-t border-gold/20"><span>Total</span><span className="font-mono text-gold">{money(order.total)}</span></div>
          </div>

          {order.messages?.length > 0 && (
            <div className="border-t border-gold/20 mt-4 pt-4">
              <p className="text-gold text-sm font-semibold mb-2">Updates from the kitchen</p>
              {order.messages.map((msg, i) => <p key={i} className="text-cream/70 text-sm">• {msg.text}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
